import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Mail, ArrowLeft, ArrowRight, Save, Send, Eye, Clock, Users,
  FileText, List, Filter, CheckCircle, AlertCircle, RefreshCw,
  Calendar, Settings, Wand2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'basics', label: 'Campaign Basics', icon: Settings },
  { id: 'audience', label: 'Select Audience', icon: Users },
  { id: 'content', label: 'Email Content', icon: FileText },
  { id: 'review', label: 'Review & Send', icon: CheckCircle },
];

export default function MarketingCampaignCreator() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  
  const [form, setForm] = useState({
    name: '',
    subject: '',
    preview_text: '',
    from_name: '',
    from_email: '',
    reply_to: '',
    template_id: '',
    html_content: '',
    plain_content: '',
    list_ids: [],
    segment_ids: [],
    campaign_type: 'one_time',
    settings: {}
  });
  
  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    if (token) {
      fetchData();
      if (isEditing) {
        fetchCampaign();
      }
    }
  }, [token, id]);

  const fetchData = async () => {
    try {
      const [listsRes, segmentsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/api/marketing/lists?token=${token}&limit=100`),
        axios.get(`${API}/api/marketing/segments?token=${token}&limit=100`),
        axios.get(`${API}/api/marketing/templates?token=${token}&limit=100`)
      ]);
      
      if (listsRes.data.success) setLists(listsRes.data.lists);
      if (segmentsRes.data.success) setSegments(segmentsRes.data.segments);
      if (templatesRes.data.success) setTemplates(templatesRes.data.templates);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(
        `${API}/api/marketing/campaigns/${id}?token=${token}`
      );
      if (response.data.success) {
        const campaign = response.data.campaign;
        setForm({
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.settings?.preview_text || '',
          from_name: campaign.from_name || '',
          from_email: campaign.from_email || '',
          reply_to: campaign.reply_to || '',
          template_id: campaign.template_id || '',
          html_content: campaign.html_content || '',
          plain_content: campaign.plain_content || '',
          list_ids: campaign.list_ids || [],
          segment_ids: campaign.segment_ids || [],
          campaign_type: campaign.campaign_type || 'one_time',
          settings: campaign.settings || {}
        });
        setRecipientCount(campaign.stats?.total_recipients || 0);
      }
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/marketing/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleList = (listId) => {
    setForm(prev => ({
      ...prev,
      list_ids: prev.list_ids.includes(listId)
        ? prev.list_ids.filter(id => id !== listId)
        : [...prev.list_ids, listId]
    }));
  };

  const toggleSegment = (segmentId) => {
    setForm(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(segmentId)
        ? prev.segment_ids.filter(id => id !== segmentId)
        : [...prev.segment_ids, segmentId]
    }));
  };

  const selectTemplate = async (templateId) => {
    updateForm('template_id', templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      updateForm('html_content', template.html_content || '');
      updateForm('plain_content', template.plain_content || '');
      if (template.subject && !form.subject) {
        updateForm('subject', template.subject);
      }
    }
  };

  const saveCampaign = async (asDraft = true) => {
    if (!form.name) {
      toast.error('Campaign name is required');
      return null;
    }
    if (!form.subject) {
      toast.error('Subject line is required');
      return null;
    }
    
    try {
      setSaving(true);
      const data = {
        ...form,
        settings: { ...form.settings, preview_text: form.preview_text }
      };
      
      let response;
      if (isEditing) {
        response = await axios.put(
          `${API}/api/marketing/campaigns/${id}?token=${token}`,
          data
        );
      } else {
        response = await axios.post(
          `${API}/api/marketing/campaigns?token=${token}`,
          data
        );
      }
      
      if (response.data.success) {
        return response.data.campaign_id || id;
      } else {
        toast.error(response.data.error || 'Failed to save campaign');
        return null;
      }
    } catch (error) {
      toast.error('Failed to save campaign');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    const campaignId = await saveCampaign(true);
    if (campaignId) {
      toast.success('Campaign saved as draft');
      navigate('/marketing/campaigns');
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Please select date and time');
      return;
    }
    
    const campaignId = await saveCampaign();
    if (!campaignId) return;
    
    try {
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
      
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/schedule?token=${token}`,
        { scheduled_at: scheduledAt.toISOString() }
      );
      
      if (response.data.success) {
        toast.success(`Campaign scheduled for ${scheduledAt.toLocaleString()}`);
        setScheduleDialogOpen(false);
        navigate('/marketing/campaigns');
      } else {
        toast.error(response.data.error || 'Failed to schedule campaign');
      }
    } catch (error) {
      toast.error('Failed to schedule campaign');
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send this campaign immediately?')) return;
    
    const campaignId = await saveCampaign();
    if (!campaignId) return;
    
    try {
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/send?token=${token}`
      );
      
      if (response.data.success) {
        toast.success(`Campaign sent! ${response.data.sent} emails delivered.`);
        navigate('/marketing/campaigns');
      } else {
        toast.error(response.data.error || 'Failed to send campaign');
      }
    } catch (error) {
      toast.error('Failed to send campaign');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Enter a test email address');
      return;
    }
    
    const campaignId = await saveCampaign();
    if (!campaignId) return;
    
    try {
      const response = await axios.post(
        `${API}/api/marketing/campaigns/${campaignId}/test?token=${token}`,
        { test_emails: [testEmail] }
      );
      
      if (response.data.success) {
        toast.success('Test email sent!');
        setTestEmailDialogOpen(false);
      } else {
        toast.error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const calculateTotalRecipients = () => {
    const listTotal = form.list_ids.reduce((sum, id) => {
      const list = lists.find(l => l.id === id);
      return sum + (list?.contact_count || 0);
    }, 0);
    
    const segmentTotal = form.segment_ids.reduce((sum, id) => {
      const segment = segments.find(s => s.id === id);
      return sum + (segment?.contact_count || 0);
    }, 0);
    
    return listTotal + segmentTotal;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return form.name && form.subject;
      case 1: return form.list_ids.length > 0 || form.segment_ids.length > 0;
      case 2: return form.html_content || form.template_id;
      case 3: return true;
      default: return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="campaign-creator">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Campaign' : 'Create Campaign'}
          </h1>
        </div>
        <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          Save Draft
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          
          return (
            <React.Fragment key={step.id}>
              <div 
                className={`flex items-center gap-2 cursor-pointer ${
                  isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                }`}
                onClick={() => setCurrentStep(index)}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-blue-100' : isCompleted ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <span className="font-medium hidden md:inline">{step.label}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded ${
                  index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Campaign Basics */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Campaign Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  placeholder="e.g., March Newsletter"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Subject Line *</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => updateForm('subject', e.target.value)}
                  placeholder="e.g., Your Weekly Update is Here!"
                />
                <p className="text-xs text-gray-500">
                  Use personalization: {"{{first_name}}"}, {"{{company}}"}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Preview Text</Label>
                <Input
                  value={form.preview_text}
                  onChange={(e) => updateForm('preview_text', e.target.value)}
                  placeholder="Brief text shown after subject in inbox"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={form.from_name}
                    onChange={(e) => updateForm('from_name', e.target.value)}
                    placeholder="Your Company"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={form.from_email}
                    onChange={(e) => updateForm('from_email', e.target.value)}
                    placeholder="noreply@company.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Reply-To Email</Label>
                <Input
                  type="email"
                  value={form.reply_to}
                  onChange={(e) => updateForm('reply_to', e.target.value)}
                  placeholder="support@company.com"
                />
              </div>
            </div>
          )}

          {/* Step 2: Select Audience */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <List className="h-5 w-5" />
                  Contact Lists
                </h3>
                {lists.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border rounded-lg">
                    <List className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No lists available</p>
                    <Button 
                      variant="link" 
                      onClick={() => navigate('/marketing/lists')}
                    >
                      Create a list first
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {lists.map((list) => (
                      <div
                        key={list.id}
                        onClick={() => toggleList(list.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          form.list_ids.includes(list.id) 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox checked={form.list_ids.includes(list.id)} />
                            <div>
                              <p className="font-medium">{list.name}</p>
                              <p className="text-sm text-gray-500">
                                {list.contact_count || 0} contacts
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Segments
                </h3>
                {segments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border rounded-lg">
                    <Filter className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No segments available</p>
                    <Button 
                      variant="link" 
                      onClick={() => navigate('/marketing/segments')}
                    >
                      Create a segment first
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        onClick={() => toggleSegment(segment.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          form.segment_ids.includes(segment.id) 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox checked={form.segment_ids.includes(segment.id)} />
                            <div>
                              <p className="font-medium">{segment.name}</p>
                              <p className="text-sm text-gray-500">
                                {segment.contact_count || 0} contacts
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Recipient Summary */}
              <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-500" />
                  <span className="font-medium">Estimated Recipients:</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {calculateTotalRecipients().toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Step 3: Email Content */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4">Choose a Template</h3>
                <div className="grid gap-3 md:grid-cols-3">
                  {templates.slice(0, 6).map((template) => (
                    <div
                      key={template.id}
                      onClick={() => selectTemplate(template.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        form.template_id === template.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-amber-500" />
                        <span className="font-medium text-sm">{template.name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {template.category}
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/marketing/templates')}
                >
                  View all templates
                </Button>
              </div>
              
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4">Or Edit HTML Directly</h3>
                <Tabs defaultValue="editor">
                  <TabsList>
                    <TabsTrigger value="editor">HTML Editor</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                  </TabsList>
                  <TabsContent value="editor" className="mt-4">
                    <Textarea
                      value={form.html_content}
                      onChange={(e) => updateForm('html_content', e.target.value)}
                      placeholder="Enter HTML content or select a template above"
                      className="font-mono text-sm h-[400px]"
                    />
                  </TabsContent>
                  <TabsContent value="preview" className="mt-4">
                    <div className="border rounded-lg bg-white">
                      {form.html_content ? (
                        <iframe
                          srcDoc={form.html_content}
                          className="w-full h-[400px] border-0"
                          title="Email Preview"
                        />
                      ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <Mail className="h-12 w-12 mx-auto mb-2" />
                            <p>Add content to see preview</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}

          {/* Step 4: Review & Send */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Campaign Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium">{form.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subject:</span>
                      <span className="font-medium truncate max-w-[200px]">{form.subject}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">From:</span>
                      <span className="font-medium">{form.from_name || 'Default'}</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Audience</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Lists:</span>
                      <span className="font-medium">{form.list_ids.length} selected</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Segments:</span>
                      <span className="font-medium">{form.segment_ids.length} selected</span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                      <span className="text-gray-500">Est. Recipients:</span>
                      <span className="font-bold text-blue-600">
                        {calculateTotalRecipients().toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Email Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Email Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg bg-gray-50 p-4">
                    <div className="mb-4 pb-4 border-b">
                      <p className="text-sm text-gray-500">Subject:</p>
                      <p className="font-medium">{form.subject}</p>
                    </div>
                    {form.html_content ? (
                      <iframe
                        srcDoc={form.html_content}
                        className="w-full h-[300px] border-0 bg-white rounded"
                        title="Email Preview"
                      />
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-gray-400 bg-white rounded">
                        <p>No content</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Send Options */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setTestEmailDialogOpen(true)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Test Email
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setScheduleDialogOpen(true)}>
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule
                  </Button>
                  <Button onClick={handleSendNow}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Now
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(s => s - 1)}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={() => setCurrentStep(s => s + 1)}
            disabled={!canProceed()}
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSendNow} disabled={!canProceed()}>
            <Send className="h-4 w-4 mr-2" />
            Send Campaign
          </Button>
        )}
      </div>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Campaign</DialogTitle>
            <DialogDescription>
              Choose when to send this campaign
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSchedule}>
              <Clock className="h-4 w-4 mr-2" />
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a preview to verify how the email looks
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Test Email Address</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTest}>
              <Send className="h-4 w-4 mr-2" />
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
